import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/api/client";
import { Link2, Loader2, Copy, Check, ExternalLink } from "lucide-react";

type Scope = "offer" | "portal";

/**
 * Mint a customer portal/offer magic-link for an order (Block 2 B2.6). The plaintext URL is
 * returned ONCE (only its hash is stored), so the panel keeps it in local state for copy/open.
 * "Offer" = the read-only quote + accept page; "Portal" = the interactive passports/payment page.
 */
export default function PortalLinkPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const [links, setLinks] = useState<Partial<Record<Scope, string>>>({});
  const [copied, setCopied] = useState<Scope | null>(null);

  const mint = useMutation({
    mutationFn: (scope: Scope) =>
      api.post<{ data: { url: string; scope: Scope } }>(
        `/api/admin/orders/${orderId}/portal-link`,
        {
          scope,
        },
      ),
    onSuccess: (res) => setLinks((m) => ({ ...m, [res.data.scope]: res.data.url })),
  });

  const copy = (scope: Scope, url: string) => {
    void navigator.clipboard.writeText(url);
    setCopied(scope);
    setTimeout(() => setCopied((v) => (v === scope ? null : v)), 1500);
  };

  const row = (scope: Scope, label: string) => (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
        <button
          onClick={() => mint.mutate(scope)}
          disabled={mint.isPending}
          className="btn-ghost btn-sm"
        >
          {mint.isPending && mint.variables === scope ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Link2 className="h-4 w-4" />
          )}{" "}
          {links[scope] ? t("orders.portal.regenerate") : t("orders.portal.createLink")}
        </button>
      </div>
      {links[scope] && (
        <div className="mt-2 flex items-center gap-2 rounded bg-gray-50 px-2 py-1.5 dark:bg-gray-800">
          <input
            readOnly
            value={links[scope]}
            className="flex-1 bg-transparent font-mono text-xs text-gray-600 dark:text-gray-300"
          />
          <button
            onClick={() => copy(scope, links[scope]!)}
            className="btn-ghost btn-sm"
            title={t("orders.portal.copyLink")}
          >
            {copied === scope ? (
              <Check className="h-4 w-4 text-emerald-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
          <a
            href={links[scope]}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost btn-sm"
            title={t("orders.portal.openLink")}
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}
    </div>
  );

  return (
    <div className="card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
        <Link2 className="h-5 w-5" /> {t("orders.portal.title")}
      </h2>
      <p className="mb-4 text-xs text-gray-500">{t("orders.portal.hint")}</p>
      <div className="space-y-4">
        {row("offer", t("orders.portal.offerRow"))}
        {row("portal", t("orders.portal.portalRow"))}
      </div>
    </div>
  );
}
